import { addBaseUrl } from '@/utils/url';

import { WagtailImage } from '@/types';

import { FilterSpec } from './types';

import { FilterParsingError } from './errors';

const WAGTAIL_RENDITION_SIGN_KEY =
  process.env.NEXT_PUBLIC_WAGTAIL_RENDITION_SIGN_KEY ?? process.env.WAGTAIL_RENDITION_SIGN_KEY ?? 'public';

// ponytail: pure-JS SHA1/HMAC because `crypto` is Node-only and this runs in
// client components (which can't be async, so Web Crypto's async API is out).
// Swap to Web Crypto if these ever move fully server-side.
function sha1(bytes: Uint8Array): Uint8Array {
  const ml = bytes.length * 8;
  // pad: append 0x80, then zeros, then 64-bit length; total % 64 === 0
  const withLen = Math.ceil((bytes.length + 9) / 64) * 64;
  const msg = new Uint8Array(withLen);
  msg.set(bytes);
  msg[bytes.length] = 0x80;
  const dv = new DataView(msg.buffer);
  dv.setUint32(withLen - 4, ml >>> 0);
  dv.setUint32(withLen - 8, Math.floor(ml / 0x100000000));

  let h0 = 0x67452301,
    h1 = 0xefcdab89,
    h2 = 0x98badcfe,
    h3 = 0x10325476,
    h4 = 0xc3d2e1f0;
  const rol = (n: number, c: number) => (n << c) | (n >>> (32 - c));
  const w = new Uint32Array(80);

  for (let i = 0; i < withLen; i += 64) {
    for (let j = 0; j < 16; j++) w[j] = dv.getUint32(i + j * 4);
    for (let j = 16; j < 80; j++) w[j] = rol(w[j - 3] ^ w[j - 8] ^ w[j - 14] ^ w[j - 16], 1);
    let a = h0,
      b = h1,
      c = h2,
      d = h3,
      e = h4;
    for (let j = 0; j < 80; j++) {
      let f, k;
      if (j < 20) {
        f = (b & c) | (~b & d);
        k = 0x5a827999;
      } else if (j < 40) {
        f = b ^ c ^ d;
        k = 0x6ed9eba1;
      } else if (j < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8f1bbcdc;
      } else {
        f = b ^ c ^ d;
        k = 0xca62c1d6;
      }
      const t = (rol(a, 5) + f + e + k + w[j]) >>> 0;
      e = d;
      d = c;
      c = rol(b, 30) >>> 0;
      b = a;
      a = t;
    }
    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }
  const out = new Uint8Array(20);
  new DataView(out.buffer).setUint32(0, h0);
  new DataView(out.buffer).setUint32(4, h1);
  new DataView(out.buffer).setUint32(8, h2);
  new DataView(out.buffer).setUint32(12, h3);
  new DataView(out.buffer).setUint32(16, h4);
  return out;
}

function hmacSha1(key: Uint8Array, msg: Uint8Array): Uint8Array {
  const block = 64;
  const k = key.length > block ? sha1(key) : key;
  const padded = new Uint8Array(block);
  padded.set(k);
  const ipad = new Uint8Array(block + msg.length);
  const opad = new Uint8Array(block + 20);
  for (let i = 0; i < block; i++) {
    ipad[i] = padded[i] ^ 0x36;
    opad[i] = padded[i] ^ 0x5c;
  }
  ipad.set(msg, block);
  opad.set(sha1(ipad), block);
  return sha1(opad);
}

/**
 * Create image signature for wagtail rendition urls.
 *
 * Imitates `generate_signature` from `wagtail.images.utils` and has to
 * create identical signatures.
 *
 * @returns Signature of image rendition to use in rendition url
 */
function generateSignature(imageId: number, spec: FilterSpec) {
  const enc = new TextEncoder();
  const url = `${imageId}/${spec}/`;
  const digest = hmacSha1(enc.encode(WAGTAIL_RENDITION_SIGN_KEY), enc.encode(url));
  let bin = '';
  for (const byte of digest) bin += String.fromCharCode(byte);
  return (
    btoa(bin)
      // Make url safe with common replacements.
      // See https://developer.mozilla.org/en-US/docs/Glossary/Base64
      .replaceAll('+', '-')
      .replaceAll('/', '_')
  );
}

/**
 * Create signed wagtail rendition urls.
 *
 * The returned url must be identical with the one that `generate_image_url`
 * from `wagtail.images.views.serve` generates.
 *
 * @returns Absolute signed url for given image rendition to fetch from wagtail.
 */
function renditionUrl(image: WagtailImage, spec: FilterSpec = 'original') {
  const signature = generateSignature(image.id, spec);
  return addBaseUrl(`/images/${signature}/${image.id}/${spec}/${image.filename}`);
}

/**
 * Parse rendition dimensions from spec.
 *
 * This is not really accurate, since it is not guaranteed,
 * that the rendition will actually have these dimensions (eg. wagtail does
 * not scale images up, if they're to small).
 *
 * All available specs are listed in the wagtail docs [0]
 *
 * `fill-`, `width-` and `height-`, `scale-`, `original` filters guarantee (if
 * the image is large enough) the dimensions and are therefore evaluated.
 *
 * [0] https://docs.wagtail.org/en/stable/topics/images.html#available-resizing-methods
 *
 * @param spec
 * @returns
 */
function parseSpec(image: WagtailImage, spec: FilterSpec) {
  // eg. width-400
  let re = /^width-([0-9]+)$/;
  let match = re.exec(spec);
  if (match) {
    return {
      width: Number(match[1]),
    };
  }

  // eg. height-400
  re = /^height-([0-9]+)$/;
  match = re.exec(spec);
  if (match) {
    return {
      height: Number(match[1]),
    };
  }

  // eg. fill-400x300
  re = /^fill-([0-9]+)x([0-9]+)$/;
  match = re.exec(spec);
  if (match) {
    return {
      width: Number(match[1]),
      height: Number(match[2]),
    };
  }

  // eg. scale-50
  re = /^scale-([0-9]+)$/;
  match = re.exec(spec);
  if (match) {
    const ratio = Number(match[1]) / 100;
    return {
      width: Math.round(image.width * ratio),
      height: Math.round(image.height * ratio),
    };
  }

  // original
  if (spec === 'original') {
    return {
      width: image.width,
      height: image.height,
    };
  }

  const allowedFilterPattern = /^[A-Za-z0-9_\-.]+$/;
  if (!allowedFilterPattern.exec(spec)) {
    throw new FilterParsingError(`Filter spec ${spec} is invalid!`);
  }

  return {};
}

/**
 * Guess dimensions using multiple hints.
 *
 * @param image The image containing information about it's original width and
 * height.
 * @param spec Wagtail filter spec for renditions which will be used to guess
 *  widths. See parseSpec().
 * @param width Enforces specific width
 * @param height Enforces specific height
 * @param fill No width or height will be returned in this case
 
* @returns The width/heights are evaluated with the following priorities:
 *  explicit width/height > fill > spec > image.width/height
 */
function guessImageDimensions(
  image: WagtailImage,
  spec?: FilterSpec,
  userDefinedWidth?: number,
  userDefinedHeight?: number,
  fill?: boolean,
) {
  if (fill) {
    return {};
  }
  let width;
  let height;

  if (spec) {
    const parsedSizes = parseSpec(image, spec);
    width = userDefinedWidth || parsedSizes.width;
    height = userDefinedHeight || parsedSizes.height;
  } else {
    width = userDefinedWidth || image.width;
    height = userDefinedHeight || image.height;
  }

  return { width, height };
}

export { guessImageDimensions, renditionUrl };
